/**
 * The onboarding wizard's steps, in order (EDUS-2681). Authoritative
 * vocabulary shared by the client stepper and any server-side step reasoning
 * (e.g. US-11 milestone restoration). Each step is owned by its own story:
 * welcome = US-2 (2681), setup-environment = US-3/4 (2682/83),
 * authorize-verify-scopes = US-5/6 (2684/85), sync-data = US-7 (2686),
 * invite-users = US-8 (2687).
 */
export const ONBOARDING_STEPS = [
  'welcome',
  'setup-environment',
  'authorize-verify-scopes',
  'sync-data',
  'invite-users',
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * Human labels for the wizard steps — shared so the wizard stepper (spog-client)
 * and the platform console's onboarding panel (spog-platform-admin-client) can't
 * drift apart.
 */
export const ONBOARDING_STEP_LABELS: Record<OnboardingStep, string> = {
  welcome: 'Welcome',
  'setup-environment': 'Setup Environment',
  'authorize-verify-scopes': 'Authorize & Verify Scopes',
  'sync-data': 'Sync Data',
  'invite-users': 'Invite Users',
};

/**
 * Fact-derived onboarding prerequisites. The wizard polls
 * GET /onboarding/progress on mount and after each step, and shows the first
 * step whose fact is still false — step selection is DERIVED from facts,
 * never latched (the routing gate, `onboarding_status`, is the latch; see
 * EDUS-2726 decision A).
 *
 * EDUS-2726 shipped a STUB that always reported the initial nothing-set-up
 * state; US-2 (EDUS-2681) makes `termsAccepted` real (derived per-user,
 * per-current-version). The remaining facts stay owned by their steps'
 * stories and are made real as those steps are built.
 */
export interface OnboardingProgressFacts {
  /**
   * The CURRENT-version Terms & Conditions have been accepted by THIS user
   * (US-2). Per-user, per-version: a secondary admin or a bumped document
   * version leaves this false, routing that user back to Step 1 (AC-2.3).
   */
  termsAccepted: boolean;
  /**
   * The tenant has ≥1 authorized (non-deleted) domain (US-3). The primary
   * domain, created at tenant setup, satisfies this on its own — secondary
   * domains are optional — so this is effectively true whenever a tenant row
   * exists. Present as an explicit fact so Step 2's completion is fact-derived
   * like the others (US-4 will additionally require `delegateConfigured`).
   */
  domainsConfigured: boolean;
  /** A delegate email is set on the customer row (wizard DWD step 1). */
  delegateConfigured: boolean;
  /** The DWD check has passed (`dwd_check_status === 'ok'`, US-6). */
  dwdConfirmed: boolean;
  /** The initial baseline sync has been kicked off (US-7). */
  initialSyncKickedOff: boolean;
}

/**
 * Where the tenant's baseline sync has got to (EDUS-2770).
 *
 * Step 4 renders a different view for each, and EDUS-2858's gate releases Step 5
 * on `complete`. Kept separate from {@link OnboardingProgressFacts} because those
 * are booleans answering "is this step done"; this answers "how did it go", which
 * `failed` and `running` both need and a boolean cannot carry.
 *
 * `failed` deliberately covers a PARTIAL run as well as an outright failure. At
 * onboarding nobody can see the data yet and a re-sync costs nothing, whereas a
 * partial is a silent gap that surfaces later as missing records — so the gate
 * holds and the admin is asked to run it again. That inverts the general advice
 * in docs/backend/job-terminal-notifications.md, deliberately and only here.
 */
export type InitialSyncStatus =
  | 'not-started'
  | 'running'
  | 'failed'
  | 'complete';

/**
 * How often Step 4 re-reads progress while a sync is running (EDUS-2858), so the
 * card moves off the spinner without the admin refreshing.
 *
 * 60s by decision (Anthony, 2026-09-11), not the 15–30s a status poll would
 * normally use: the sync is measured in minutes to hours, and nobody is expected
 * to sit here at all — the design promises an email and invites them to close the
 * tab. This serves the minority who stay. Polling is additionally skipped while
 * the tab is hidden, for the same reason the lock heartbeat is.
 *
 * Each poll hits `GET /onboarding/progress`, which records the caller as an email
 * recipient **only while the status is `running`**. Do not loosen that guard (see
 * `onboarding.controller.ts`) — an unconditional re-add would put people back on
 * a list the callback has already drained, and a redelivery would email them
 * twice.
 */
export const ONBOARDING_SYNC_POLL_INTERVAL_MS = 60_000;

/**
 * How far the Step-3 DWD propagation re-check window runs (EDUS-2844).
 *
 * Google's own ceiling for a new domain-wide-delegation grant reaching its
 * services. Both ends read this: the server decides when to stop re-checking and
 * send the stalled email, and the wizard decides which of the two Step-3 failure
 * screens to render.
 */
export const DWD_RETRY_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Where the tenant's DWD propagation re-check window has got to (EDUS-2844).
 *
 * A new DWD grant can take up to 24 hours to propagate, so the first Verify after
 * granting is EXPECTED to fail. Rather than leaving the admin to come back and
 * press the button on a hunch, a failed scope check opens a window: we re-check
 * hourly, email when it lands, and email again if it never does.
 *
 * Derived from `customers.dwd_retry_started_at` alone, which is why it survives
 * the admin closing the tab — Step 3 renders the same screen for someone who
 * stayed and someone who came back via the email.
 *
 * `expired` is deliberately not the same as "no window": the window is left set
 * once it elapses precisely so the terminal screen has something to render.
 */
export type DwdRetryStatus = 'none' | 'retrying' | 'expired';

/**
 * Derive {@link DwdRetryStatus} from the window start.
 *
 * Shared rather than implemented twice: the server answers the progress read with
 * it and the wizard re-derives it locally between polls, so the elapsed counter
 * can tick and flip the screen at 24h without waiting for a round-trip.
 */
export function deriveDwdRetryStatus(
  startedAt: string | Date | null | undefined,
  now: number = Date.now()
): DwdRetryStatus {
  if (!startedAt) {
    return 'none';
  }
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) {
    return 'none';
  }
  return now - started >= DWD_RETRY_WINDOW_MS ? 'expired' : 'retrying';
}

/**
 * Result of kicking off the wizard's Step-4 initial baseline sync (US-7,
 * EDUS-2686). `kickedOff` is true both when this call enqueued the sync and
 * when it was already running (idempotent) — the wizard only needs "the sync is
 * going," not whether this exact call started it.
 */
export interface OnboardingSyncResult {
  kickedOff: boolean;
}

export interface OnboardingProgress {
  customerId: number | null;
  domain: string;
  facts: OnboardingProgressFacts;
  /**
   * How the tenant's baseline sync is going (EDUS-2770). Step 4 picks its view
   * from this; EDUS-2858's gate releases Step 5 on `complete`.
   */
  initialSyncStatus: InitialSyncStatus;
  /**
   * Where the Step-3 DWD propagation re-check window has got to (EDUS-2844).
   * Step 3 picks between its two failure screens with this.
   */
  dwdRetryStatus: DwdRetryStatus;
  /**
   * ISO instant the re-check window opened, or null when none is open. Feeds the
   * retry screen's "Current elapsed time" counter, which ticks client-side —
   * the poll refreshes the facts, it does not drive the clock.
   */
  dwdRetryStartedAt: string | null;
  /**
   * The DWD SCOPE check has passed (`dwd_check_status === 'ok'`).
   *
   * Deliberately NOT {@link OnboardingProgressFacts.dwdConfirmed}, which also
   * requires this user to have been provisioned as a super admin (US-9). The
   * re-check chain can only ever establish this half — it has an email address,
   * not a session — so conflating the two would let a poll walk a non-super-admin
   * into Step 4. The wizard uses it to know a background re-check succeeded and
   * it should re-run the interactive verify.
   */
  dwdScopesVerified: boolean;
  /**
   * ISO expiry of the tenant's license, for the wizard footer's "License for
   * Gopher expires on …" line (EDUS-2681). Best-effort: null when the license
   * lookup is unavailable — never fails the progress read.
   */
  licenseExpiry: string | null;
}
